import { supabase } from '../lib/supabase';

/**
 * Progress Tracking Service
 * Manages user progress across different features (Assessment, Questionnaire, AI Plan)
 */
class ProgressTrackingService {
  
  /**
   * Get user progress data
   */
  async getUserProgress(userId) {
    try {
      const { data, error } = await supabase
        .from('user_progress')
        .select('*')
        .eq('user_id', userId)
        .single();

      if (error && error.code !== 'PGRST116') { // PGRST116 = no rows found
        console.error('Error fetching user progress:', error);
        return { success: false, error: error.message };
      }

      // Return default progress if no data found
      const defaultProgress = {
        assessment_completed: false,
        questionnaire_completed: false,
        ai_plan_generated: false,
        last_updated: new Date().toISOString()
      };

      return { 
        success: true, 
        data: data || defaultProgress 
      };
    } catch (error) {
      console.error('Error in getUserProgress:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Update user progress
   */
  async updateUserProgress(userId, progressData) {
    try {
      const progressUpdate = {
        user_id: userId,
        ...progressData,
        last_updated: new Date().toISOString()
      };

      // Use upsert to insert or update
      const { data, error } = await supabase
        .from('user_progress')
        .upsert(progressUpdate, { 
          onConflict: 'user_id' 
        })
        .select()
        .single();

      if (error) {
        console.error('Error updating user progress:', error);
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      console.error('Error in updateUserProgress:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Mark assessment as completed
   */
  async markAssessmentCompleted(userId) {
    return await this.updateUserProgress(userId, {
      assessment_completed: true
    });
  }

  /**
   * Mark questionnaire as completed
   */
  async markQuestionnaireCompleted(userId) {
    return await this.updateUserProgress(userId, {
      questionnaire_completed: true
    });
  }

  /**
   * Mark AI plan as generated
   */
  async markAIPlanGenerated(userId) {
    return await this.updateUserProgress(userId, {
      ai_plan_generated: true
    });
  }

  /**
   * Check if user has completed assessment
   */
  async hasCompletedAssessment(userId) {
    const result = await this.getUserProgress(userId);
    return result.success ? result.data.assessment_completed : false;
  }

  /**
   * Check if user has completed questionnaire
   */
  async hasCompletedQuestionnaire(userId) {
    const result = await this.getUserProgress(userId);
    return result.success ? result.data.questionnaire_completed : false;
  }

  /**
   * Check if user has generated AI plan
   */
  async hasGeneratedAIPlan(userId) {
    const result = await this.getUserProgress(userId);
    return result.success ? result.data.ai_plan_generated : false;
  }

  /**
   * Get completion status for header navigation
   */
  async getNavigationProgress(userId) {
    const progress = await this.getUserProgress(userId);
    if (!progress.success) {
      return {
        assessment_completed: false,
        questionnaire_completed: false,
        ai_plan_generated: false
      };
    }

    return {
      assessment_completed: progress.data.assessment_completed || false,
      questionnaire_completed: progress.data.questionnaire_completed || false,
      ai_plan_generated: progress.data.ai_plan_generated || false
    };
  }
}

// Create and export a singleton instance
const progressTrackingService = new ProgressTrackingService();
export default progressTrackingService;

